import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { LogOut, Users, Package, Mail, Search, Home, ShieldCheck, Download, MessageSquare, Send, ArrowLeft, Bell, BellOff } from 'lucide-react';
import { company } from '../mock';
import { playChime, requestNotificationPermission, showBrowserNotification } from '../utils/notify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'vedyah_admin_token';

function useAdminApi(token) {
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onLogin(r.data.token);
    } catch (e2) {
      setErr('Invalid credentials. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 notranslate" translate="no" style={{ background: 'linear-gradient(135deg, #eeeafb 0%, #ffffff 100%)' }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_24px_60px_-15px_rgba(79,70,229,0.35)] border border-white p-8">
        <div className="flex items-center gap-3 mb-6">
          <img src={company.logo} alt="Vedyah" className="h-12 w-12 rounded-xl shadow-lg" />
          <div>
            <div className="font-bold text-[color:var(--brand)]">VEDYAH EXIM</div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">Admin Portal</div>
          </div>
        </div>
        <h2 className="text-2xl font-semibold mb-1">Sign in</h2>
        <p className="text-sm text-[color:var(--muted)] mb-6">Enter your admin credentials to continue.</p>
        <form onSubmit={submit} className="space-y-4">
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" className="h-12 w-full px-4 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition shadow-inner" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="h-12 w-full px-4 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition shadow-inner" />
          {err && <div className="text-sm text-red-600 bg-red-50 p-2 rounded-lg border border-red-100">{err}</div>}
          <button disabled={loading} className="h-12 w-full rounded-xl bg-gradient-to-br from-[color:var(--brand)] to-[color:var(--brand-2)] text-white font-medium hover:brightness-110 transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-[0_10px_24px_-6px_rgba(79,70,229,0.5)]">
            <ShieldCheck size={18} /> {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <Link to="/" className="mt-6 flex items-center gap-2 text-xs text-[color:var(--muted)] hover:text-[color:var(--brand)] transition-colors">
          <Home size={12} /> Back to website
        </Link>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'contacts', label: 'Contact Enquiries', icon: Users, exportUrl: 'contacts.csv' },
  { id: 'quotes', label: 'Quote Requests', icon: Package, exportUrl: 'quotes.csv' },
  { id: 'newsletter', label: 'Newsletter', icon: Mail, exportUrl: 'newsletter.csv' },
  { id: 'chats', label: 'Live Chat', icon: MessageSquare },
];

function ChatDetail({ api, session, onBack, onRefreshList }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/admin/chats/${session.id}/messages`);
      setMessages(r.data);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 999999 }), 40);
    } catch {}
  };

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [session.id]); // eslint-disable-line

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/chats/${session.id}/reply`, { text: text.trim() });
      setText('');
      await load();
      onRefreshList && onRefreshList();
    } finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 h-[560px]">
      <div className="flex items-center gap-3 border-b border-slate-200 p-4">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>
        <div className="ml-auto text-right">
          <div className="font-semibold">{session.name}</div>
          <div className="text-xs text-slate-500">{session.email}</div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3 bg-slate-50">
        {messages.map((m) => {
          const mine = m.sender === 'agent';
          const align = mine ? 'justify-end' : 'justify-start';
          const bg = m.sender === 'agent' ? 'bg-emerald-500 text-white' : m.sender === 'bot' ? 'bg-white border border-slate-200 text-slate-800' : 'bg-[color:var(--brand)] text-white';
          return (
            <div key={m.id} className={`flex ${align}`}>
              <div className={`max-w-[70%] px-4 py-2 rounded-lg text-sm shadow-sm ${bg}`}>
                <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1">{m.sender === 'agent' ? 'You (Agent)' : m.sender === 'bot' ? 'Auto reply' : session.name}</div>
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                <div className="text-[10px] mt-1 opacity-70">{new Date(m.createdAt).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
        {messages.length === 0 && <div className="text-center text-slate-500 py-16">No messages yet.</div>}
      </div>
      <form onSubmit={send} className="p-4 border-t border-slate-200 flex gap-2 bg-white">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your reply..." className="flex-1 h-11 px-4 rounded-md border border-slate-200 outline-none focus:border-[color:var(--brand)] transition" />
        <button disabled={sending || !text.trim()} className="h-11 px-5 rounded-md bg-[color:var(--brand)] text-white hover:bg-[color:var(--brand-2)] transition-colors flex items-center gap-2 disabled:opacity-60">
          <Send size={16} /> {sending ? '...' : 'Reply'}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const api = useAdminApi(token);
  const [tab, setTab] = useState('contacts');
  const [stats, setStats] = useState({ contacts: 0, quotes: 0, newsletter: 0, chats: 0 });
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true); // true only until the first load finishes
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('vedyah_sound') !== 'off');
  const soundOnRef = useRef(soundOn); // read inside the 10s polling callback (avoids a stale closure)
  const chatsRef = useRef({}); // { sessionId: { messageCount, name } }
  const initedRef = useRef(false);

  useEffect(() => {
    soundOnRef.current = soundOn;
    localStorage.setItem('vedyah_sound', soundOn ? 'on' : 'off');
  }, [soundOn]);

  useEffect(() => {
    // Ask for browser notification permission once
    if (soundOn) requestNotificationPermission();
  }, [soundOn]);

  const handleNewChatActivity = (fresh) => {
    // Compare with previous snapshot to detect new visitor messages
    if (!initedRef.current) {
      // First load: just seed snapshot, no alert
      fresh.forEach((c) => { chatsRef.current[c.id] = { messageCount: c.messageCount, name: c.name }; });
      initedRef.current = true;
      return;
    }
    let newVisitorMsg = null;
    fresh.forEach((c) => {
      const prev = chatsRef.current[c.id];
      if (!prev) {
        // brand new session
        newVisitorMsg = { name: c.name, text: c.lastMessage || 'started a chat' };
      } else if (c.messageCount > prev.messageCount && c.unreadForAdmin > 0) {
        newVisitorMsg = { name: c.name, text: c.lastMessage };
      }
      chatsRef.current[c.id] = { messageCount: c.messageCount, name: c.name };
    });
    if (newVisitorMsg && soundOnRef.current) {
      playChime();
      showBrowserNotification(`New message from ${newVisitorMsg.name}`, newVisitorMsg.text || '');
    }
  };

  const loadAll = async () => {
    try {
      const [s, c, qu, n, ch] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/contacts'),
        api.get('/admin/quotes'),
        api.get('/admin/newsletter'),
        api.get('/admin/chats'),
      ]);
      setStats(s.data);
      setContacts(c.data);
      setQuotes(qu.data);
      setNewsletter(n.data);
      setChats(ch.data);
      handleNewChatActivity(ch.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); const t = setInterval(loadAll, 10000); return () => clearInterval(t); }, []); // eslint-disable-line

  const downloadCsv = async (filename) => {
    try {
      const r = await api.get(`/admin/export/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const matches = (obj, query) => {
    if (!query) return true;
    const s = JSON.stringify(obj).toLowerCase();
    return s.includes(query.toLowerCase());
  };

  const fmtDate = (d) => new Date(d).toLocaleString();

  const filteredContacts = contacts.filter((c) => matches(c, q));
  const filteredQuotes = quotes.filter((c) => matches(c, q));
  const filteredNewsletter = newsletter.filter((c) => matches(c, q));
  const filteredChats = chats.filter((c) => matches(c, q));

  const currentTab = TABS.find((t) => t.id === tab);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-white notranslate" translate="no">
      <header className="bg-white/80 backdrop-blur border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={company.logo} alt="Vedyah" className="h-10 w-10 rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)]" />
            <div>
              <div className="font-bold text-[color:var(--brand)] text-sm">VEDYAH EXIM</div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Admin Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setSoundOn((v) => { const nv = !v; if (nv) { playChime(); requestNotificationPermission(); } return nv; }); }}
              className={`text-sm flex items-center gap-1 transition-colors ${soundOn ? 'text-emerald-600 hover:text-emerald-700' : 'text-slate-400 hover:text-slate-600'}`}
              title={soundOn ? 'Notifications on (click to mute)' : 'Notifications muted (click to enable)'}
            >
              {soundOn ? <Bell size={16} /> : <BellOff size={16} />}
              <span className="hidden sm:inline">{soundOn ? 'On' : 'Muted'}</span>
            </button>
            <Link to="/" className="text-sm text-[color:var(--muted)] hover:text-[color:var(--brand)] flex items-center gap-1 transition-colors"><Home size={14} /> View Site</Link>
            <button onClick={onLogout} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-8">
          {[
            { label: 'Contact Enquiries', value: stats.contacts, icon: Users, grad: 'from-blue-500 to-indigo-600', glow: 'shadow-blue-500/30' },
            { label: 'Quote Requests', value: stats.quotes, icon: Package, grad: 'from-fuchsia-500 to-purple-600', glow: 'shadow-purple-500/30' },
            { label: 'Newsletter', value: stats.newsletter, icon: Mail, grad: 'from-emerald-500 to-teal-600', glow: 'shadow-emerald-500/30' },
            { label: 'Live Chats', value: stats.chats, icon: MessageSquare, grad: 'from-amber-500 to-orange-600', glow: 'shadow-amber-500/30' },
          ].map((s, i) => {
            const I = s.icon;
            return (
              <div
                key={i}
                className="relative bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4
                  shadow-[0_8px_24px_-8px_rgba(15,23,42,0.15)] hover:shadow-[0_16px_32px_-8px_rgba(15,23,42,0.25)]
                  hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br ${s.grad} text-white
                  shadow-lg ${s.glow} ring-4 ring-white`}
                  style={{ boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -3px 6px rgba(0,0,0,0.15), 0 10px 20px -6px rgba(0,0,0,0.25)' }}
                >
                  <I size={24} />
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-slate-800 leading-none">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs + Search + Export */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_10px_30px_-12px_rgba(15,23,42,0.15)]">
          <div className="flex flex-col md:flex-row md:items-center gap-3 border-b border-slate-100 p-4 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex gap-1.5 flex-wrap">
              {TABS.map((tt) => {
                const I = tt.icon;
                const active = tab === tt.id;
                return (
                  <button
                    key={tt.id}
                    onClick={() => { setTab(tt.id); setActiveChat(null); }}
                    className={`px-4 py-2 rounded-xl text-sm flex items-center gap-2 transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-br from-[color:var(--brand)] to-[color:var(--brand-2)] text-white shadow-[0_6px_16px_-4px_rgba(79,70,229,0.5)] -translate-y-0.5'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <I size={15} /> {tt.label}
                  </button>
                );
              })}
            </div>
            <div className="md:ml-auto flex items-center gap-2 w-full md:w-auto">
              {tab !== 'chats' && (
                <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-slate-200 flex-1 md:flex-none md:w-56 shadow-inner">
                  <Search size={16} className="text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="bg-transparent outline-none text-sm flex-1" />
                </div>
              )}
              {currentTab?.exportUrl && (
                <button onClick={() => downloadCsv(currentTab.exportUrl)} className="h-10 px-4 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white text-sm hover:from-emerald-600 hover:to-emerald-700 flex items-center gap-2 transition-colors shadow-[0_6px_16px_-4px_rgba(16,185,129,0.5)]">
                  <Download size={15} /> Export CSV
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading && <div className="p-10 text-center text-slate-500">Loading...</div>}

            {!loading && tab === 'contacts' && (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                    <th className="text-left px-4 py-3 font-medium">Name</th>
                    <th className="text-left px-4 py-3 font-medium">Email</th>
                    <th className="text-left px-4 py-3 font-medium">Phone</th>
                    <th className="text-left px-4 py-3 font-medium">Subject</th>
                    <th className="text-left px-4 py-3 font-medium">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.length === 0 && <tr><td colSpan="6" className="text-center text-slate-500 py-10">No enquiries yet.</td></tr>}
                  {filteredContacts.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(c.createdAt)}</td>
                      <td className="px-4 py-3 font-medium">{c.name}</td>
                      <td className="px-4 py-3"><a href={`mailto:${c.email}`} className="text-[color:var(--brand)] hover:underline">{c.email}</a></td>
                      <td className="px-4 py-3">{c.phone || '-'}</td>
                      <td className="px-4 py-3">{c.subject || '-'}</td>
                      <td className="px-4 py-3 max-w-xs truncate" title={c.message}>{c.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && tab === 'quotes' && (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                    <th className="text-left px-4 py-3 font-medium">Ref</th>
                    <th className="text-left px-4 py-3 font-medium">Company / Name</th>
                    <th className="text-left px-4 py-3 font-medium">Route</th>
                    <th className="text-left px-4 py-3 font-medium">Container</th>
                    <th className="text-left px-4 py-3 font-medium">Commodity</th>
                    <th className="text-left px-4 py-3 font-medium">Valid From</th>
                    <th className="text-left px-4 py-3 font-medium">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.length === 0 && <tr><td colSpan="8" className="text-center text-slate-500 py-10">No quote requests yet.</td></tr>}
                  {filteredQuotes.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(c.createdAt)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[color:var(--brand)]">{c.quoteRef}</td>
                      <td className="px-4 py-3 font-medium">{c.companyName || c.name}</td>
                      <td className="px-4 py-3">{c.startLocation} ({c.startType}) → {c.endLocation} ({c.endType})</td>
                      <td className="px-4 py-3">{c.containerQty} × {c.containerType}{c.dangerousGoods ? ' • DG' : ''}{c.soc ? ' • SOC' : ''}</td>
                      <td className="px-4 py-3">{c.commodity}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{c.validFrom}</td>
                      <td className="px-4 py-3"><a href={`mailto:${c.email}`} className="text-[color:var(--brand)] hover:underline">{c.email}</a>{c.phone ? <div className="text-xs text-slate-500">{c.phone}</div> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && tab === 'newsletter' && (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                    <th className="text-left px-4 py-3 font-medium">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNewsletter.length === 0 && <tr><td colSpan="2" className="text-center text-slate-500 py-10">No subscribers yet.</td></tr>}
                  {filteredNewsletter.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(c.createdAt)}</td>
                      <td className="px-4 py-3"><a href={`mailto:${c.email}`} className="text-[color:var(--brand)] hover:underline">{c.email}</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && tab === 'chats' && !activeChat && (
              <div>
                {filteredChats.length === 0 && <div className="p-10 text-center text-slate-500">No chat sessions yet.</div>}
                <ul className="divide-y divide-slate-100">
                  {filteredChats.map((c) => (
                    <li key={c.id} className="p-4 flex items-center gap-4 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setActiveChat(c)}>
                      <div className="w-11 h-11 rounded-full bg-[color:var(--brand-soft)] text-[color:var(--brand)] flex items-center justify-center font-semibold">{(c.name || '?')[0].toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-slate-800 truncate">{c.name}</div>
                          {c.unreadForAdmin > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white">{c.unreadForAdmin} new</span>}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{c.email}</div>
                        <div className="text-sm text-slate-600 truncate mt-1">{c.lastMessage}</div>
                      </div>
                      <div className="text-xs text-slate-500 text-right shrink-0">
                        <div>{fmtDate(c.lastMessageAt)}</div>
                        <div className="mt-1">{c.messageCount} msgs</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!loading && tab === 'chats' && activeChat && (
              <ChatDetail api={api} session={activeChat} onBack={() => setActiveChat(null)} onRefreshList={loadAll} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => logout());
  }, [token, logout]);

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
